import { useState, useCallback, useEffect } from 'react';
import { getWebConfig, saveWebConfig } from '../services/web-config-service';
import { WebBlock, WebBlockType, WebConfig, ThemePreset } from '../models';

export function useBuilder() {
    const [blocks, setBlocks] = useState<WebBlock[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [webConfig, setWebConfig] = useState<WebConfig | null>(null);
    const [themePreset, setThemePreset] = useState<ThemePreset>('elegant');

    useEffect(() => {
        getWebConfig().then(config => {
            setBlocks(config.blocks || []);
            setWebConfig(config);
            setThemePreset(config.themePreset ?? 'elegant');
            setIsLoaded(true);
        });
    }, []);

    const save = useCallback(async () => {
        setIsSaving(true);
        await saveWebConfig({ blocks, themePreset });
        setIsSaving(false);
    }, [blocks, themePreset]);

    const updateThemePreset = useCallback((preset: ThemePreset) => {
        setThemePreset(preset);
        setWebConfig(prev => prev ? { ...prev, themePreset: preset } : prev);
    }, []);

    const addBlock = useCallback((type: WebBlockType) => {
        let props: any = { bgColor: '#ffffff' };
        if (type === 'hero') props.text = 'Titular principal';
        else if (type === 'text') props.text = 'Descripción o texto';
        else if (type === 'contact') props.title = 'Contáctanos';
        else if (type === 'reservation') props.title = 'Reserva tu cita';
        else if (type === 'business_info') {
            props.title = 'Nuestra Información';
            props.showAddress = true;
            props.showPhone = true;
            props.showHours = true;
            props.showEmail = true;
            props.showMapLink = true;
        } else if (type === 'gallery') {
            props.title = 'Nuestra Galería';
            props.images = [
                { url: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=400&q=80', alt: 'Corte 1' },
                { url: 'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?auto=format&fit=crop&w=400&q=80', alt: 'Corte 2' },
                { url: 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&w=400&q=80', alt: 'Corte 3' },
            ];
            props.columns = 3;
            props.layout = 'grid';
        }

        const newBlock = {
            id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type,
            props
        };
        setBlocks(prev => [...prev, newBlock]);
        setSelectedId(newBlock.id);
    }, []);

    const updateBlock = useCallback((id: string, props: any) => {
        setBlocks(prev => prev.map(b => b.id === id ? { ...b, props: { ...b.props, ...props } } : b));
    }, []);

    const removeBlock = useCallback((id: string) => {
        setBlocks(prev => prev.filter(b => b.id !== id));
        if (selectedId === id) setSelectedId(null);
    }, [selectedId]);

    const moveBlock = useCallback((id: string, direction: 'up' | 'bottom') => {
        setBlocks(prev => {
            const index = prev.findIndex(b => b.id === id);
            if (index === -1) return prev;

            const newIndex = direction === 'up' ? index - 1 : index + 1;
            if (newIndex < 0 || newIndex >= prev.length) return prev;

            const newBlocks = [...prev];
            const [moved] = newBlocks.splice(index, 1);
            newBlocks.splice(newIndex, 0, moved);
            return newBlocks;
        });
    }, []);

    return {
        blocks, setBlocks,
        selectedId, setSelectedId,
        isLoaded, isSaving,
        publicApiBaseUrl: webConfig?.publicApiBaseUrl,
        themePreset,
        businessData: webConfig ? {
            name: webConfig.businessName,
            address: webConfig.address,
            phone: webConfig.phone,
            email: webConfig.email,
            hours: webConfig.hoursText,
        } : null,
        save, addBlock, updateBlock, removeBlock, moveBlock,
        updateThemePreset
    };
}
